import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useT } from '@/contexts/I18nContext';
import {
  getCameraVendorParams,
  patchCameraVendorParams,
  type VendorParamSpec,
  type VendorParamsTarget,
  type VendorParamValue,
} from '@/api/vendorParams';
import { extractApiError } from '@/utils/apiError';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  cameraId: number | string;
  className?: string;
}

type ValueMap = Record<string, VendorParamValue | null>;

/**
 * Vendor-API parameter tweaks panel (issue #34 part 3).
 *
 * Collapsed by default. The inner Form (which probe_http's the device)
 * only mounts when the operator expands the header — keeps the
 * camera-edit dialog from hammering the device on every open.
 *
 * Schema + values are device-driven (BE probes ISAPI
 * /Image/channels/<n>/capabilities). The response is split into two
 * targets:
 *   - `nvr`: the curated subset Hik's NVR admin UI exposes natively
 *     (brightness/contrast/saturation, WDR, BLC, HLC). Whatever the
 *     operator can already adjust through the NVR's own web UI.
 *   - `camera`: everything else the camera firmware lists in /caps
 *     (Shutter, SupplementLight, ImageFlip, ...). Conceptually
 *     camera-side; the writes still go through the NVR until the
 *     direct camera path is wired up.
 *
 * Rules baked into the FE rendering:
 *   - Form doesn't render until first SWR fetch has settled — old
 *     values would otherwise flash in before the live values arrive
 *     and the operator could tweak a stale slider mid-load.
 *   - WDR ⊕ BLC are mutually exclusive on Hik firmware (NVR's UI
 *     hides BLC when WDR is on and vice versa). We hide the
 *     contradicting knobs based on current draft state.
 *   - Refresh button explicitly re-probes the device.
 */
export function VendorParamsPanel({ cameraId, className }: Props) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full items-center gap-2 rounded-md py-1 text-left hover:bg-muted/50"
      >
        {expanded ? (
          <ChevronDown className="size-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" />
        )}
        <div className="flex-1">
          <p className="text-sm font-medium">{t('equipment.vendorParams.title')}</p>
          {!expanded && (
            <p className="text-xs text-muted-foreground">
              {t('equipment.vendorParams.clickToOpen')}
            </p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-2">
          <Form cameraId={cameraId} />
        </div>
      )}
    </div>
  );
}

function Form({ cameraId }: { cameraId: number | string }) {
  const { t } = useT();
  const swrKey = `equipment:vendor-params:${cameraId}`;
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    swrKey,
    () => getCameraVendorParams(cameraId).then(r => r.data),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  // Don't render the form until SWR's first fetch settles. Without
  // this the cached payload (or just empty initial state) flashes in
  // and the operator can start dragging a slider while a stale value
  // is still on screen. An error settles too — otherwise a failed
  // fetch (422 unsupported, camera offline) leaves the loading text
  // on screen forever instead of the error/unsupported message.
  const [firstSettled, setFirstSettled] = useState(false);
  useEffect(() => {
    if (firstSettled) return;
    if ((data || error) && !isValidating) setFirstSettled(true);
  }, [data, error, isValidating, firstSettled]);

  const targets = data?.targets ?? [];
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);
  const activeTarget = useMemo<VendorParamsTarget | null>(() => {
    if (!targets.length) return null;
    return targets.find(t_ => t_.id === activeTargetId) ?? targets[0];
  }, [targets, activeTargetId]);

  const handleRefresh = () => {
    setFirstSettled(false);
    void mutate();
  };

  if (isLoading || !firstSettled) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        {t('equipment.vendorParams.loadingValues')}
      </div>
    );
  }

  if (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 422) {
      return (
        <p className="text-xs text-muted-foreground">
          {t('equipment.vendorParams.unsupported')}
        </p>
      );
    }
    return (
      <div className="space-y-2">
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {extractApiError(error, t('equipment.vendorParams.loadError'))}
        </div>
        <RefreshButton onClick={handleRefresh} t={t} validating={isValidating} />
      </div>
    );
  }

  if (!data || !activeTarget) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {data?.errors?.join(' · ') ?? t('equipment.vendorParams.loadError')}
        </p>
        <RefreshButton onClick={handleRefresh} t={t} validating={isValidating} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          {targets.length > 1 && (
            <TargetSelector
              targets={targets}
              active={activeTarget.id}
              onChange={setActiveTargetId}
              t={t}
            />
          )}
        </div>
        <RefreshButton onClick={handleRefresh} t={t} validating={isValidating} />
      </div>
      <TargetForm
        cameraId={cameraId}
        target={activeTarget}
        onSaved={() => void mutate()}
        t={t}
      />
    </div>
  );
}

function RefreshButton({
  onClick, t, validating,
}: {
  onClick: () => void;
  t: (k: string) => string;
  validating: boolean;
}) {
  return (
    <Tooltip content={t('equipment.vendorParams.refresh')}><button
      type="button"
      onClick={onClick}
      aria-label={t('equipment.vendorParams.refresh')}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      disabled={validating}
    >
      <RefreshCw className={`size-3.5 ${validating ? 'animate-spin' : ''}`} />
    </button></Tooltip>
  );
}

function TargetSelector({
  targets, active, onChange, t,
}: {
  targets: VendorParamsTarget[];
  active: string;
  onChange: (id: string) => void;
  t: (k: string) => string;
}) {
  return (
    <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
      {targets.map(target => (
        <button
          key={target.id}
          type="button"
          onClick={() => onChange(target.id)}
          className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
            target.id === active
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:bg-background/50'
          }`}
        >
          {t(target.label_key)}
        </button>
      ))}
    </div>
  );
}

function TargetForm({
  cameraId, target, onSaved, t,
}: {
  cameraId: number | string;
  target: VendorParamsTarget;
  onSaved: () => void;
  t: (k: string) => string;
}) {
  const [draft, setDraft] = useState<ValueMap>(target.values);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [patchError, setPatchError] = useState<string | null>(null);
  const debounceRef = useRef<Record<string, number>>({});
  // Track which target the draft was initialised from. We re-seed
  // when the operator switches tabs OR when an explicit refresh
  // pulls fresh values from the device, but NOT on every revalidation
  // (avoid wiping in-progress slider drags).
  const lastTargetIdRef = useRef<string>(target.id);

  useEffect(() => {
    if (lastTargetIdRef.current !== target.id) {
      lastTargetIdRef.current = target.id;
      setDraft(target.values);
      Object.values(debounceRef.current).forEach(window.clearTimeout);
      debounceRef.current = {};
      setPatchError(null);
    }
  }, [target]);

  useEffect(() => () => {
    Object.values(debounceRef.current).forEach(window.clearTimeout);
  }, []);

  const scheduleWrite = (key: string, value: VendorParamValue) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    if (debounceRef.current[key] != null) {
      window.clearTimeout(debounceRef.current[key]);
    }
    debounceRef.current[key] = window.setTimeout(() => {
      void writeOne(key, value);
    }, 350);
  };

  const writeOne = async (key: string, value: VendorParamValue) => {
    setSavingKeys(prev => new Set(prev).add(key));
    setPatchError(null);
    try {
      const resp = await patchCameraVendorParams(
        cameraId, { [key]: value }, target.id,
      );
      if (resp.data.errors?.length) {
        setPatchError(resp.data.errors.join(' · '));
      }
      onSaved();
    } catch (e) {
      const msg = isAxiosError(e)
        ? extractApiError(e, t('equipment.vendorParams.writeError'))
        : t('equipment.vendorParams.writeError');
      setPatchError(msg);
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Hide knobs that the current draft state makes irrelevant.
  // WDR ⊕ BLC on Hik firmware: turning either on disables the other.
  // (NVR's own UI works the same way.) WDR + HLC is allowed, no rule.
  const wdrOn = isWdrOn(draft);
  const blcOn = isBlcOn(draft);
  const visibleSpecs = useMemo(() =>
    target.schema.filter(spec => {
      if (wdrOn && spec.group === 'BLC') return false;
      if (blcOn && spec.group === 'WDR') return false;
      return true;
    }),
    [target.schema, wdrOn, blcOn],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, VendorParamSpec[]>();
    for (const spec of visibleSpecs) {
      const list = map.get(spec.group) ?? [];
      list.push(spec);
      map.set(spec.group, list);
    }
    return Array.from(map.entries());
  }, [visibleSpecs]);

  if (target.schema.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {target.errors?.join(' · ') ?? t('equipment.vendorParams.loadError')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map(([group, specs]) => (
        <div key={group} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {labelFor(t, group)}
          </p>
          <div className="grid gap-3 pl-1">
            {specs.map(spec => (
              <Knob
                key={spec.key}
                spec={spec}
                value={draft[spec.key] ?? null}
                saving={savingKeys.has(spec.key)}
                onChange={v => scheduleWrite(spec.key, v)}
                t={t}
              />
            ))}
          </div>
        </div>
      ))}

      {target.errors?.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {target.errors.join(' · ')}
        </p>
      )}
      {patchError && (
        <p className="mt-1 text-xs text-destructive">{patchError}</p>
      )}
    </div>
  );
}

function isWdrOn(values: ValueMap): boolean {
  const mode = values['WDR.mode'];
  // Anything other than the explicit off-tokens counts as on. Hik
  // ships "close" as the off value; some firmwares also use "OFF".
  if (typeof mode !== 'string') return false;
  return mode.toLowerCase() !== 'close' && mode.toLowerCase() !== 'off';
}

function isBlcOn(values: ValueMap): boolean {
  if (values['BLC.enabled'] === true) return true;
  const mode = values['BLC.BLCMode'];
  if (typeof mode === 'string' && mode.toUpperCase() !== 'CLOSE') return true;
  return false;
}

function Knob({
  spec, value, saving, onChange, t,
}: {
  spec: VendorParamSpec;
  value: VendorParamValue | null;
  saving: boolean;
  onChange: (v: VendorParamValue) => void;
  t: (k: string) => string;
}) {
  const label = labelFor(t, spec.key);

  if (spec.kind === 'bool') {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`vp-${spec.key}`}
          checked={value === true}
          onChange={e => onChange(e.target.checked)}
          className="cursor-pointer"
        />
        <Label htmlFor={`vp-${spec.key}`}>{label}</Label>
        {saving && <Loader2 className="size-3 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  if (spec.kind === 'enum') {
    const options = spec.options ?? [];
    const currentString = typeof value === 'string' ? value : value == null ? '' : String(value);
    const allOptions = currentString && !options.includes(currentString)
      ? [currentString, ...options]
      : options;
    return (
      <div className="grid gap-1">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor={`vp-${spec.key}`}>{label}</Label>
          {saving && <Loader2 className="size-3 animate-spin text-muted-foreground" />}
        </div>
        <Select
          id={`vp-${spec.key}`}
          value={currentString}
          onChange={e => onChange(e.target.value)}
        >
          {allOptions.map(opt => (
            <option key={opt} value={opt}>{labelForOption(t, spec.key, opt)}</option>
          ))}
        </Select>
      </div>
    );
  }

  // int slider — hide if the device omitted this knob in its read
  // response (the disabled-slider UX confused operators).
  if (value === null) return null;
  const numVal = typeof value === 'number' ? value : 0;
  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={`vp-${spec.key}`}>{label}</Label>
        <div className="flex items-center gap-1.5">
          {saving && <Loader2 className="size-3 animate-spin text-muted-foreground" />}
          <span className="w-8 text-right font-mono text-xs text-muted-foreground">
            {numVal}
          </span>
        </div>
      </div>
      <input
        id={`vp-${spec.key}`}
        type="range"
        min={spec.min ?? 0}
        max={spec.max ?? 100}
        value={numVal}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full"
      />
    </div>
  );
}

// Label dictionary for Hik image-knob keys we recognise — used both
// for group headers and field labels. Anything not in this map falls
// through to a humanised version of the raw dotted key.
const KEY_LABEL_I18N: Record<string, string> = {
  WDR: 'equipment.vendorParams.wdr',
  'WDR.mode': 'equipment.vendorParams.wdr.mode',
  'WDR.WDRLevel': 'equipment.vendorParams.wdr.level',
  BLC: 'equipment.vendorParams.blc',
  'BLC.enabled': 'equipment.vendorParams.blc.enabled',
  'BLC.BLCMode': 'equipment.vendorParams.blc.mode',
  'BLC.BLCLevel': 'equipment.vendorParams.blc.level',
  HLC: 'equipment.vendorParams.hlc',
  'HLC.enabled': 'equipment.vendorParams.hlc.enabled',
  'HLC.HLCLevel': 'equipment.vendorParams.hlc.level',
  Color: 'equipment.vendorParams.color',
  'Color.brightnessLevel': 'equipment.vendorParams.brightness',
  'Color.contrastLevel': 'equipment.vendorParams.contrast',
  'Color.saturationLevel': 'equipment.vendorParams.saturation',
  'Color.sharpnessLevel': 'equipment.vendorParams.sharpness',
  'Color.hueLevel': 'equipment.vendorParams.hue',
  Shutter: 'equipment.vendorParams.shutter',
  'Shutter.ShutterLevel': 'equipment.vendorParams.shutter.level',
  SupplementLight: 'equipment.vendorParams.supplementLight',
  'SupplementLight.supplementLightMode': 'equipment.vendorParams.supplementLight.mode',
  'SupplementLight.whiteLightBrightness': 'equipment.vendorParams.supplementLight.whiteBrightness',
  'SupplementLight.irLightBrightness': 'equipment.vendorParams.supplementLight.irBrightness',
  ImageFlip: 'equipment.vendorParams.imageFlip',
  'ImageFlip.enabled': 'equipment.vendorParams.imageFlip.enabled',
  'ImageFlip.ImageFlipStyle': 'equipment.vendorParams.imageFlip.style',
};

const OPTION_LABEL_I18N: Record<string, Record<string, string>> = {
  'WDR.mode': {
    auto: 'equipment.vendorParams.wdr.mode.auto',
    close: 'equipment.vendorParams.wdr.mode.off',
    open: 'equipment.vendorParams.wdr.mode.on',
  },
  'BLC.BLCMode': {
    CLOSE: 'equipment.vendorParams.blc.mode.off',
    UP: 'equipment.vendorParams.blc.mode.up',
    DOWN: 'equipment.vendorParams.blc.mode.down',
    LEFT: 'equipment.vendorParams.blc.mode.left',
    RIGHT: 'equipment.vendorParams.blc.mode.right',
    CENTER: 'equipment.vendorParams.blc.mode.center',
  },
  'SupplementLight.supplementLightMode': {
    close: 'equipment.vendorParams.supplementLight.mode.off',
    irLight: 'equipment.vendorParams.supplementLight.mode.ir',
    colorVuWhiteLight: 'equipment.vendorParams.supplementLight.mode.colorVu',
    eventIntelligence: 'equipment.vendorParams.supplementLight.mode.eventIntel',
  },
};

function labelFor(t: (k: string) => string, key: string): string {
  const i18n = KEY_LABEL_I18N[key];
  if (i18n) {
    const v = t(i18n);
    if (v !== i18n) return v;
  }
  return humanise(key);
}

function labelForOption(t: (k: string) => string, key: string, opt: string): string {
  const map = OPTION_LABEL_I18N[key];
  if (map && map[opt]) {
    const v = t(map[opt]);
    if (v !== map[opt]) return v;
  }
  return opt;
}

function humanise(key: string): string {
  const last = key.split('.').pop() ?? key;
  const spaced = last.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
